import clsx from 'clsx';
import { RefObject, useCallback, useEffect, useState } from 'react';

type ScrollEdgeFadeProps = Readonly<{
  scrollRef: RefObject<HTMLDivElement | null>;
  edge: 'top' | 'bottom';
  className?: string;
}>;

export default function ScrollEdgeFade({
  scrollRef,
  edge,
  className
}: ScrollEdgeFadeProps) {
  const [visible, setVisible] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (edge === 'top') {
      setVisible(el.scrollTop > 1);
    } else {
      setVisible(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    }
  }, [scrollRef, edge]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });

    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [scrollRef, updateScrollState]);

  return (
    <div
      aria-hidden='true'
      className={clsx(
        'pointer-events-none absolute transition-opacity duration-75',
        edge === 'top'
          ? 'bg-gradient-to-b from-gray-100 to-transparent'
          : 'bg-gradient-to-t from-gray-100 to-transparent',
        visible ? 'opacity-100' : 'opacity-0',
        className
      )}
    />
  );
}
